import type { DataStore } from "../../Data/Store"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import type { ILogResponse } from "../../Types/Generated/LogbookModel/Log"
import type { IDayResponse } from "../../Types/Generated/LogbookModel/Day"
import type { IAppResponse } from "../../Types/Generated/LogbookApp/Routes"
import type { DayWriters } from "../../App/Context"
import { LogApi } from "../../Types/Generated/LogbookApi/LogApi"
import { DayApi } from "../../Types/Generated/LogbookApi/DayApi"

// Identifiers a DayView writer bundle is scoped to: the ship + the one day it edits,
// plus the trip a new day is created under. `logId` stays a per-call argument since a
// day holds many logs, each edited independently.
export interface DayWriterIds {
  shipId: number;
  dayId: number;
  tripId: number;
}

// New (flag-on) DayView writers: each write goes through `DataStore.mutate`, so a log
// field edit lands optimistically at once, the typed-REST response reconciles the
// authoritative row, and a REST failure rolls the optimistic edit back (guarded
// against a racing broadcast). The typed endpoints are the single source of the wire
// contract — shared with the legacy variant below and the retained `*ViewData`
// statics. Engine/sails updates carry no entity in their response, so they have no
// optimism and rely on the centralized live broadcast; day update/create reconcile
// the returned `Day` for forward-compatibility (DayView doesn't read Day from the
// store today, so there is nothing to show optimistically).
export function createStoreDayWriters(store: DataStore, ids: DayWriterIds): DayWriters {
  const { shipId, dayId, tripId } = ids;
  return {
    updateLog(logId, values) {
      const current = store.getEntity(SchemaEntities.Log, logId);
      const optimistic = current ? { upsert: { Log: [{ ...current, ...values }] } } : undefined;
      return store.mutate<ILogResponse>({
        optimistic,
        request: (client) => new LogApi(client).logs.update({ params: { shipId, logId }, body: values }).call(),
        reconcile: (r) => ({ entities: { Log: [r.log] } }),
      });
    },
    updateLogEngine(logId, engine, on) {
      return store.mutate<IAppResponse>({
        request: (client) => new LogApi(client).logs.updateEngine({ params: { shipId, logId }, body: { engine, on } }).call(),
      });
    },
    updateLogSails(logId, sails) {
      return store.mutate<IAppResponse>({
        request: (client) => new LogApi(client).logs.updateSails({ params: { shipId, logId }, body: { sails } }).call(),
      });
    },
    updateDay(values) {
      return store.mutate<IDayResponse>({
        request: (client) => new DayApi(client).days.update({ params: { shipId, dayId }, body: values }).call(),
        reconcile: (r) => ({ entities: { Day: [r.day] } }),
      });
    },
    createDay(values) {
      return store.mutate<IDayResponse>({
        request: (client) => new DayApi(client).days.create({ params: { shipId, tripId }, body: values }).call(),
        reconcile: (r) => ({ entities: { Day: [r.day] } }),
      });
    },
    calculateDay() {
      return store.mutate<IAppResponse>({
        request: (client) => new DayApi(client).days.calculate({ params: { shipId, dayId } }).call(),
      });
    },
  };
}

// Inert DayView writers for harnesses and tests that render a DayView subtree but never
// exercise a write (the real variants provide working writers). Each rejects loudly if
// unexpectedly called, so an accidental save in a read-only fixture surfaces rather than
// silently no-opping.
const rejectNoop = (name: string) => (): Promise<never> =>
  Promise.reject(new Error(`noopDayWriters.${name}: no writer provided (read-only DayView context)`));

export const noopDayWriters: DayWriters = {
  updateLog: rejectNoop("updateLog"),
  updateLogEngine: rejectNoop("updateLogEngine"),
  updateLogSails: rejectNoop("updateLogSails"),
  updateDay: rejectNoop("updateDay"),
  createDay: rejectNoop("createDay"),
  calculateDay: rejectNoop("calculateDay"),
};
